// src/components/companies/CompanyDetails.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  TelcoFair · Company Details
//  "TelcoFair Glass Workspace" — same system as Companies.jsx / Dashboard.jsx
//  Layout is pure CSS Grid with the same design tokens and primitives
// ─────────────────────────────────────────────────────────────────────────────

import React, {
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    IconButton,
    Chip,
    Avatar,
    Tabs,
    Tab,
    Dialog,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Tooltip,
    Skeleton,
    Fade,
    Divider,
    InputAdornment,
    CircularProgress,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Business as BusinessIcon,
    LocationOn as LocationIcon,
    Language as LanguageIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    People as PeopleIcon,
    VerifiedUser as VerifiedUserIcon,
    CheckCircle as CheckCircleIcon,
    Block as BlockIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    CalendarToday as CalendarIcon,
    Assessment as AssessmentIcon,
    Group as GroupIcon,
    EventNote as EventNoteIcon,
    Speed as SpeedIcon,
    Shield as ShieldIcon,
    Save as SaveIcon,
    Close as CloseIcon,
    Subscriptions as SubscriptionsIcon,
    Payment as PaymentIcon,
    Receipt as ReceiptIcon,
    Inventory2 as PackageIcon,
    FiberManualRecord as LiveIcon,
    Upload as UploadIcon,
    Download as DownloadIcon,
    ExpandMore as ExpandMoreIcon,
    AdminPanelSettings as AdminPanelSettingsIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db, storage } from '../../config/firebase';
import {
    doc,
    getDoc,
    updateDoc,
    deleteDoc,
    serverTimestamp,
    collection,
    query,
    where,
    getDocs,
    orderBy,
    limit,
    addDoc,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { safeExternalUrl } from '../../utils/safeNavigation';
import { uploadAdminFile } from '../../services/firebase/storage';

/* ═══════════════════════════════════════════════════════════════════════════
   1 · DESIGN TOKENS (identical to Companies.jsx)
   ═══════════════════════════════════════════════════════════════════════════ */

const palette = {
    canvas: '#F2F7F9',
    canvasDeep: '#E7F0F4',
    paper: '#FFFFFF',
    ink: '#182235',
    inkSoft: '#506175',
    inkLight: '#7B8B9C',
    inkFaint: '#AAB6C1',
    hairline: 'rgba(31, 62, 82, 0.09)',
    hairlineSoft: 'rgba(31, 62, 82, 0.055)',

    rust: 'var(--primary, #2E6F8E)',
    rustDeep: 'var(--secondary, #42B89B)',
    rustGlow: 'var(--primary, #2E6F8E)',

    amber: '#D78B28',
    amberGlow: '#F1B75F',

    pine: '#2E8B69',
    pineGlow: '#69B99A',

    teal: '#268AA3',
    tealGlow: '#64B8C9',

    plum: '#8D5AC8',
    plumGlow: '#B78BDF',

    indigo: '#596BCE',
    indigoGlow: '#8796E2',

    success: '#1C8A56',
    warning: '#D18A0E',
    danger: '#C9342B',
};

const ACCENTS = [palette.rust, palette.teal, palette.amber, palette.pine, palette.plum, palette.indigo];

const fontDisplay = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontBody = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontMono = "'JetBrains Mono', 'SFMono-Regular', monospace";

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');`;

/* ═══════════════════════════════════════════════════════════════════════════
   2 · HELPERS (from Companies.jsx)
   ═══════════════════════════════════════════════════════════════════════════ */

const hexToRgb = (hex) => {
    if (typeof hex !== 'string') return { r: 22, g: 18, b: 16 };
    let h = hex.replace('#', '').trim();
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return { r: 22, g: 18, b: 16 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
};

const rgba = (color, a = 1) => {
    if (typeof color === 'string' && color.startsWith('var(')) return `color-mix(in srgb, ${color} ${Math.max(0, Math.min(100, Number(a) * 100))}%, transparent)`;
    if (typeof color === 'string' && color.startsWith('rgb')) return color;
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value?.toDate === 'function') {
        try { return value.toDate(); } catch { return null; }
    }
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
};

const formatDate = (value) => {
    const d = toDate(value);
    if (!d) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const compact = (n) => {
    const v = Number(n) || 0;
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 10_000) return `${(v / 1000).toFixed(1)}K`;
    return Math.round(v).toLocaleString();
};

const COMPANY_TYPES = [
    { value: 'operator', label: 'Operator', accent: palette.rust },
    { value: 'vendor', label: 'Vendor', accent: palette.plum },
    { value: 'aggregator', label: 'Aggregator', accent: palette.teal },
    { value: 'solution_provider', label: 'Solution Provider', accent: palette.pine },
    { value: 'consultant', label: 'Consultant', accent: palette.amber },
    { value: 'mvno', label: 'MVNO', accent: palette.indigo },
    { value: 'reseller', label: 'Reseller', accent: palette.tealGlow },
];

const typeMeta = (value) =>
    COMPANY_TYPES.find((t) => t.value === value) || {
        value: value || 'other',
        label: value ? String(value).replace(/_/g, ' ') : 'Unspecified',
        accent: palette.inkLight,
    };

const LIFECYCLE_META = {
    active: { label: 'Active', tone: palette.success },
    pending: { label: 'Pending', tone: palette.warning },
    inactive: { label: 'Inactive', tone: palette.inkLight },
    blocked: { label: 'Blocked', tone: palette.danger },
};

const PAYMENT_META = {
    not_required: { label: 'Not required', tone: palette.inkLight },
    paid: { label: 'Paid', tone: palette.success },
    approved: { label: 'Approved', tone: palette.success },
    pending: { label: 'Pending', tone: palette.warning },
    review: { label: 'Under review', tone: palette.indigo },
    rejected: { label: 'Rejected', tone: palette.danger },
    manual: { label: 'Manual', tone: palette.plum },
};

const SUBSCRIPTION_META = {
    active: { label: 'Active', tone: palette.success },
    pending_payment: { label: 'Payment pending', tone: palette.warning },
    trial: { label: 'Trial', tone: palette.indigo },
    one_time: { label: 'One-time', tone: palette.amber },
    expired: { label: 'Expired', tone: palette.danger },
    cancelled: { label: 'Cancelled', tone: palette.inkLight },
    inactive: { label: 'None', tone: palette.inkLight },
};

const lifecycleOf = (c) => {
    if (c?.isBlocked) return 'blocked';
    if (c?.isActive === false) return 'inactive';
    if (!c?.isVerified) return 'pending';
    return 'active';
};

const currencySymbol = (code) => {
    const symbols = { EUR: '€', GBP: '£', USD: '$', INR: '₹', AED: 'د.إ' };
    return symbols[String(code || '').toUpperCase()] || '$';
};

const CYCLE_SUFFIX = {
    monthly: '/mo',
    annual: '/yr',
    yearly: '/yr',
    quarterly: '/qtr',
};

const priceLabel = (subscription) => {
    if (!subscription) return '—';
    const amount = Number(subscription.price) || 0;
    return `${currencySymbol(subscription.currency || 'EUR')}${amount.toLocaleString()}${CYCLE_SUFFIX[String(subscription.billingCycle || '').toLowerCase()] || ''}`;
};

/* ═══════════════════════════════════════════════════════════════════════════
   3 · LIQUID PRIMITIVES (from Companies.jsx)
   ═══════════════════════════════════════════════════════════════════════════ */

const liquid = (accent = palette.rust, { depth = 1, radius = 26 } = {}) => ({
    position: 'relative',
    borderRadius: `${radius}px`,
    background: `linear-gradient(155deg, ${rgba('#FFFFFF', 0.82)} 0%, ${rgba('#FFFFFF', 0.55)} 42%, ${rgba(accent, 0.07)} 100%)`,
    backdropFilter: 'blur(22px) saturate(180%)',
    WebkitBackdropFilter: 'blur(22px) saturate(180%)',
    border: `1px solid ${rgba('#FFFFFF', 0.6)}`,
    boxShadow: [
        `inset 0 1px 0 ${rgba('#FFFFFF', 0.95)}`,
        `inset 0 -1px 0 ${rgba('#FFFFFF', 0.35)}`,
        `inset 0 0 0 1px ${rgba(accent, 0.06)}`,
        `0 ${2 * depth}px ${6 * depth}px ${rgba(palette.ink, 0.04)}`,
        `0 ${10 * depth}px ${30 * depth}px -${8 * depth}px ${rgba(accent, 0.16)}`,
    ].join(', '),
    overflow: 'hidden',
    transition: 'box-shadow .5s cubic-bezier(.22,1,.36,1), transform .5s cubic-bezier(.22,1,.36,1), border-color .4s ease',
});

const useSheen = () => {
    const ref = useRef(null);
    const onPointerMove = useCallback((e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
        el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
        el.style.setProperty('--sheen', '1');
    }, []);
    const onPointerLeave = useCallback(() => {
        const el = ref.current;
        if (el) el.style.setProperty('--sheen', '0');
    }, []);
    return { ref, onPointerMove, onPointerLeave };
};

const sheenLayer = (accent) => ({
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 'var(--sheen, 0)',
    transition: 'opacity .45s ease',
    background: `radial-gradient(420px circle at var(--mx, 50%) var(--my, 0%), ${rgba('#FFFFFF', 0.75)}, ${rgba(accent, 0.10)} 38%, transparent 68%)`,
    mixBlendMode: 'soft-light',
});

const glassField = (accent = palette.rust) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        fontSize: '0.875rem',
        background: rgba('#FFFFFF', 0.6),
        backdropFilter: 'blur(8px)',
        transition: 'box-shadow .3s ease',
        '& fieldset': { borderColor: palette.hairline, borderWidth: '1.5px' },
        '&:hover fieldset': { borderColor: rgba(accent, 0.4) },
        '&.Mui-focused fieldset': { borderColor: accent, borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 4px ${rgba(accent, 0.12)}` },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
});

const GlassIcon = ({ icon, color, size = 42 }) => (
    <Box
        sx={{
            width: size, height: size, flexShrink: 0,
            borderRadius: `${size * 0.34}px`,
            display: 'grid', placeItems: 'center',
            color: '#fff',
            background: `linear-gradient(150deg, ${rgba(color, 0.95)}, ${rgba(color, 0.72)})`,
            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.55)}, inset 0 -2px 6px ${rgba(palette.ink, 0.18)}, 0 8px 20px -6px ${rgba(color, 0.55)}`,
            '& svg': { fontSize: size * 0.48, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.18))' },
        }}
    >
        {icon}
    </Box>
);

const GlassPanel = ({
    accent = palette.rust,
    delay = 0,
    lift = false,
    padding = { xs: 2.25, md: 2.75 },
    children,
    sx = {},
    ...rest
}) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    return (
        <motion.div
            initial={{ opacity: 0, y: 22, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', width: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent),
                    p: padding,
                    height: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    '&::after': sheenLayer(accent),
                    ...(lift && {
                        '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 26px 56px -18px ${rgba(accent, 0.34)}`,
                        },
                    }),
                    ...sx,
                }}
                {...rest}
            >
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
            </Paper>
        </motion.div>
    );
};

const PanelHead = ({ icon, title, subtitle, accent = palette.rust, chip, right, dense = false }) => (
    <Box
        sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 1.5, flexWrap: 'wrap', mb: dense ? 1.75 : 2.5,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {icon && <GlassIcon icon={icon} color={accent} size={dense ? 36 : 42} />}
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: fontDisplay, fontWeight: 600,
                        fontSize: dense ? '1.02rem' : '1.14rem',
                        color: palette.ink, letterSpacing: '-0.015em', lineHeight: 1.15,
                    }}
                >
                    {title}
                </Typography>
                {subtitle ? (
                    <Typography sx={{ fontSize: '0.76rem', color: palette.inkLight, mt: 0.25 }}>{subtitle}</Typography>
                ) : (
                    <Box sx={{ width: dense ? 34 : 44, height: 3, mt: 0.75, borderRadius: 3, background: `linear-gradient(90deg, ${accent}, ${rgba(accent, 0)})` }} />
                )}
            </Box>
            {chip && (
                <Chip
                    label={chip}
                    size="small"
                    sx={{
                        height: 24, fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 600,
                        letterSpacing: '0.04em', textTransform: 'uppercase',
                        color: accent, bgcolor: rgba(accent, 0.09),
                        border: `1px solid ${rgba(accent, 0.16)}`,
                    }}
                />
            )}
        </Box>
        {right}
    </Box>
);

const StatCard = ({ label, value, icon, accent, delta, hint, delay = 0, loading }) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const displayValue = typeof value === 'number' ? compact(value) : value;

    return (
        <motion.div
            initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent, { radius: 24 }),
                    height: '100%', p: { xs: 2, md: 2.4 },
                    display: 'flex', flexDirection: 'column', minWidth: 0,
                    '&::after': sheenLayer(accent),
                    '&:hover': {
                        transform: 'translateY(-7px) scale(1.012)',
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 28px 60px -20px ${rgba(accent, 0.42)}`,
                    },
                    '&:hover .tf-orb': { opacity: 0.4, transform: 'scale(1.25)' },
                }}
            >
                <Box
                    className="tf-orb"
                    sx={{
                        position: 'absolute', top: -70, right: -70, width: 170, height: 170,
                        borderRadius: '50%', filter: 'blur(38px)', opacity: 0.16,
                        background: `radial-gradient(circle, ${accent}, transparent 68%)`,
                        transition: 'opacity .6s ease, transform .6s cubic-bezier(.22,1,.36,1)',
                        pointerEvents: 'none',
                    }}
                />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.75 }}>
                        <GlassIcon icon={icon} color={accent} size={40} />
                        {delta && (
                            <Chip
                                size="small"
                                icon={delta.trend === 'down' ? <TrendingDownIcon sx={{ fontSize: 13 }} /> : <TrendingUpIcon sx={{ fontSize: 13 }} />}
                                label={delta.label}
                                sx={{
                                    height: 22, fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 700,
                                    color: delta.trend === 'up' ? palette.success : palette.danger,
                                    bgcolor: rgba(delta.trend === 'up' ? palette.success : palette.danger, 0.09),
                                    border: `1px solid ${rgba(delta.trend === 'up' ? palette.success : palette.danger, 0.18)}`,
                                    '& .MuiChip-icon': { color: delta.trend === 'up' ? palette.success : palette.danger, ml: '6px' },
                                }}
                            />
                        )}
                    </Box>

                    {loading ? (
                        <Skeleton width="60%" height={40} sx={{ bgcolor: palette.hairline }} />
                    ) : (
                        <Typography
                            sx={{
                                fontFamily: fontDisplay, fontWeight: 600,
                                fontSize: { xs: '1.85rem', md: '2.2rem' },
                                lineHeight: 1, letterSpacing: '-0.03em',
                                background: `linear-gradient(140deg, ${palette.ink} 20%, ${accent})`,
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {displayValue}
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            mt: 0.9, fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 500,
                            textTransform: 'uppercase', letterSpacing: '0.12em', color: palette.inkSoft,
                        }}
                    >
                        {label}
                    </Typography>
                    {hint && (
                        <Typography sx={{ mt: 'auto', pt: 1.5, fontSize: '0.68rem', color: palette.inkLight, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                                component="span"
                                sx={{
                                    width: 6, height: 6, borderRadius: '50%', bgcolor: accent,
                                    boxShadow: `0 0 0 3px ${rgba(accent, 0.16)}`,
                                    animation: 'tfPulse 2.4s ease-in-out infinite',
                                }}
                            />
                            {hint}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </motion.div>
    );
};

const MetaPill = ({ label, tone = palette.inkLight, pulse = false, icon }) => (
    <Box
        sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.55,
            px: 1, py: 0.35, borderRadius: '9px', whiteSpace: 'nowrap',
            fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 600,
            letterSpacing: '0.04em', textTransform: 'uppercase',
            color: tone, bgcolor: rgba(tone, 0.1), border: `1px solid ${rgba(tone, 0.2)}`,
        }}
    >
        {icon
            ? React.cloneElement(icon, { sx: { fontSize: 12 } })
            : <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: tone, animation: pulse ? 'tfPulse 2.4s ease-in-out infinite' : 'none' }} />}
        {label}
    </Box>
);

const LifecyclePill = ({ company }) => {
    const meta = LIFECYCLE_META[lifecycleOf(company)] || { label: 'Unknown', tone: palette.inkLight };
    return <MetaPill label={meta.label} tone={meta.tone} pulse={lifecycleOf(company) === 'active'} />;
};

const PaymentPill = ({ status }) => {
    const meta = PAYMENT_META[status] || { label: status || 'Unknown', tone: palette.inkLight };
    return <MetaPill label={meta.label} tone={meta.tone} />;
};

const SubscriptionPill = ({ status }) => {
    const meta = SUBSCRIPTION_META[status] || { label: status || 'None', tone: palette.inkLight };
    return <MetaPill label={meta.label} tone={meta.tone} />;
};

const TypePill = ({ type }) => {
    const meta = typeMeta(type);
    return <MetaPill label={meta.label} tone={meta.accent} />;
};

const CompanyAvatar = ({ company, size = 40, radius = 14 }) => {
    const accent = company?.isBlocked ? palette.inkLight : typeMeta(company?.companyType).accent;
    return (
        <Avatar
            src={company?.logo || company?.profileImage || ''}
            sx={{
                width: size, height: size, borderRadius: `${radius}px`, flexShrink: 0,
                fontFamily: fontMono, fontSize: size * 0.34, fontWeight: 700, color: '#fff',
                background: `linear-gradient(150deg, ${accent}, ${rgba(accent, 0.68)})`,
                boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.4)}, 0 8px 18px -8px ${rgba(accent, 0.85)}`,
                filter: company?.isBlocked ? 'grayscale(0.6)' : 'none',
            }}
        >
            {(company?.companyName || 'C').charAt(0).toUpperCase()}
        </Avatar>
    );
};

const DetailLine = ({ icon, label, value, accent }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.85 }}>
        <Box
            sx={{
                width: 28, height: 28, borderRadius: '9px', display: 'grid', placeItems: 'center', flexShrink: 0,
                color: accent, bgcolor: rgba(accent, 0.1), border: `1px solid ${rgba(accent, 0.16)}`,
                '& svg': { fontSize: 15 },
            }}
        >
            {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.inkFaint }}>
                {label}
            </Typography>
            <Box sx={{ fontSize: '0.84rem', color: palette.ink, fontWeight: 500, overflowWrap: 'anywhere' }}>
                {value}
            </Box>
        </Box>
    </Box>
);

const EmptyState = ({ icon, title, text, action, onAction }) => (
    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
        <Box sx={{ display: 'grid', placeItems: 'center', mb: 1.5 }}>
            <GlassIcon icon={icon} color={palette.inkFaint} size={54} />
        </Box>
        {title && (
            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.05rem', color: palette.ink }}>
                {title}
            </Typography>
        )}
        <Typography sx={{ color: palette.inkLight, fontSize: '0.85rem', mt: 0.5 }}>{text}</Typography>
        {action && (
            <Button
                startIcon={<AddIcon />}
                onClick={onAction}
                sx={{
                    mt: 2.25, textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.5, py: 1,
                    color: '#fff',
                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 14px 30px -14px ${rgba(palette.rust, 1)}`,
                    '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                }}
            >
                {action}
            </Button>
        )}
    </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════
   4 · LIQUID BACKDROP
   ═══════════════════════════════════════════════════════════════════════════ */

const LiquidBackdrop = React.memo(function LiquidBackdrop() {
    const motes = useMemo(
        () =>
            Array.from({ length: 16 }, (_, i) => ({
                id: i,
                size: 3 + ((i * 5) % 8),
                x: (i * 41) % 100,
                y: (i * 59) % 100,
                duration: 28 + ((i * 4) % 16),
                delay: (i * 1.9) % 12,
                color: ACCENTS[i % ACCENTS.length],
            })),
        []
    );

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <Box component="svg" sx={{ position: 'absolute', width: 0, height: 0 }} aria-hidden>
                <defs>
                    <filter id="tf-co-liquid">
                        <feTurbulence type="fractalNoise" baseFrequency="0.009 0.013" numOctaves="2" seed="11" result="noise">
                            <animate attributeName="baseFrequency" dur="32s" values="0.009 0.013;0.015 0.007;0.009 0.013" repeatCount="indefinite" />
                        </feTurbulence>
                        <feDisplacementMap in="SourceGraphic" in2="noise" scale="66" xChannelSelector="R" yChannelSelector="G" />
                    </filter>
                </defs>
            </Box>

            <motion.div
                animate={{ x: [0, -60, 40, 0], y: [0, 40, -30, 0], scale: [1, 1.12, 0.92, 1] }}
                transition={{ duration: 31, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '-8%', left: '-5%',
                    width: 600, height: 600, borderRadius: '50%',
                    background: `radial-gradient(circle at 40% 40%, ${rgba(palette.rustGlow, 0.3)}, ${rgba(palette.rust, 0.12)} 46%, transparent 72%)`,
                    filter: 'url(#tf-co-liquid) blur(44px)',
                }}
            />
            <motion.div
                animate={{ x: [0, 60, -35, 0], y: [0, -40, 30, 0], scale: [1, 0.9, 1.14, 1] }}
                transition={{ duration: 37, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', bottom: '-12%', right: '-6%',
                    width: 560, height: 560, borderRadius: '50%',
                    background: `radial-gradient(circle at 55% 45%, ${rgba(palette.plumGlow, 0.22)}, ${rgba(palette.teal, 0.12)} 48%, transparent 72%)`,
                    filter: 'url(#tf-co-liquid) blur(50px)',
                }}
            />
            <motion.div
                animate={{ x: ['-50%', '-56%', '-45%', '-50%'], y: ['-50%', '-45%', '-56%', '-50%'], scale: [1, 1.07, 0.95, 1] }}
                transition={{ duration: 27, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '42%', left: '52%',
                    width: 720, height: 720, borderRadius: '50%',
                    background: `radial-gradient(circle, ${rgba(palette.amberGlow, 0.15)}, transparent 66%)`,
                    filter: 'blur(80px)',
                }}
            />

            {motes.map((m) => (
                <motion.span
                    key={m.id}
                    initial={{ opacity: 0 }}
                    animate={{
                        y: [`${m.y}%`, `${m.y - 40}%`, `${m.y}%`],
                        x: [`${m.x}%`, `${m.x + 12}%`, `${m.x}%`],
                        opacity: [0, 0.45, 0],
                        scale: [0.4, 1, 0.4],
                    }}
                    transition={{ duration: m.duration, delay: m.delay, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        position: 'absolute', top: 0, left: 0,
                        width: m.size, height: m.size, borderRadius: '50%',
                        background: `radial-gradient(circle at 30% 30%, #fff, ${m.color})`,
                        boxShadow: `0 0 12px ${rgba(m.color, 0.5)}`,
                    }}
                />
            ))}

            <Box
                sx={{
                    position: 'absolute', inset: 0, opacity: 0.5,
                    backgroundImage: `linear-gradient(${rgba(palette.ink, 0.022)} 1px, transparent 1px),
                                      linear-gradient(90deg, ${rgba(palette.ink, 0.022)} 1px, transparent 1px)`,
                    backgroundSize: '72px 72px',
                    maskImage: 'radial-gradient(circle at 50% 25%, #000 0%, transparent 78%)',
                    WebkitMaskImage: 'radial-gradient(circle at 50% 25%, #000 0%, transparent 78%)',
                }}
            />
        </Box>
    );
});

/* ═══════════════════════════════════════════════════════════════════════════
   5 · TAB PANEL
   ═══════════════════════════════════════════════════════════════════════════ */

const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index} style={{ width: '100%' }}>
        {value === index && <Box sx={{ py: 3, width: '100%' }}>{children}</Box>}
    </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   6 · MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const CompanyDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [company, setCompany] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [editDialog, setEditDialog] = useState(false);
    const [editData, setEditData] = useState({});
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingCover, setUploadingCover] = useState(false);
    const [companyMeetings, setCompanyMeetings] = useState([]);
    const [meetingsLoading, setMeetingsLoading] = useState(false);
    const [paymentProofs, setPaymentProofs] = useState([]);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [teamMembers, setTeamMembers] = useState([]);
    const [teamLoading, setTeamLoading] = useState(false);
    const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState(false);

    /* ── fetch data ───────────────────────────────────────────────────────── */

    const fetchCompany = useCallback(async () => {
        setLoading(true);
        try {
            const docRef = doc(db, 'companies', id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const companyData = { id: docSnap.id, ...docSnap.data() };
                setCompany(companyData);
                setEditData(companyData);
                await Promise.all([
                    fetchCompanyMeetings(companyData),
                    fetchPaymentProofs(companyData),
                    fetchTeamMembers(companyData),
                ]);
            } else {
                toast.error('Company not found');
                navigate('/companies');
            }
        } catch (error) {
            console.error('Error fetching company:', error);
            toast.error('Failed to load company details');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    const fetchCompanyMeetings = async (companyData) => {
        setMeetingsLoading(true);
        try {
            const meetingsRef = collection(db, 'meetings');
            const q = query(
                meetingsRef,
                where('targetCompanyId', '==', companyData.id),
                orderBy('scheduledAt', 'desc'),
                limit(20)
            );
            const snapshot = await getDocs(q);
            const meetings = [];
            snapshot.forEach((doc) => {
                meetings.push({ id: doc.id, ...doc.data() });
            });
            setCompanyMeetings(meetings);
        } catch (error) {
            console.warn('Error fetching company meetings:', error);
            setCompanyMeetings([]);
        } finally {
            setMeetingsLoading(false);
        }
    };

    const fetchPaymentProofs = async (companyData) => {
        setPaymentLoading(true);
        try {
            const proofsRef = collection(db, 'paymentProofs');
            const q = query(
                proofsRef,
                where('companyId', '==', companyData.id),
                orderBy('createdAt', 'desc')
            );
            const snapshot = await getDocs(q);
            const proofs = [];
            snapshot.forEach((doc) => {
                proofs.push({ id: doc.id, ...doc.data() });
            });
            setPaymentProofs(proofs);
        } catch (error) {
            console.warn('Error fetching payment proofs:', error);
            setPaymentProofs([]);
        } finally {
            setPaymentLoading(false);
        }
    };

    const fetchTeamMembers = async (companyData) => {
        setTeamLoading(true);
        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('companyId', '==', companyData.id));
            const snapshot = await getDocs(q);
            const members = [];
            snapshot.forEach((doc) => {
                members.push({ id: doc.id, userId: doc.id, ...doc.data() });
            });
            setTeamMembers(members);
        } catch (error) {
            console.warn('Error fetching team members:', error);
            setTeamMembers([]);
        } finally {
            setTeamLoading(false);
        }
    };

    useEffect(() => {
        if (id) fetchCompany();
    }, [id, fetchCompany]);

    /* ── actions ──────────────────────────────────────────────────────────── */

    const handleTabChange = (_, newValue) => setTabValue(newValue);

    const handleSaveEdit = async () => {
        try {
            await updateDoc(doc(db, 'companies', id), {
                companyName: editData.companyName,
                description: editData.description,
                companyType: editData.companyType,
                companySize: editData.companySize,
                headquarters: editData.headquarters,
                website: editData.website,
                regions: editData.regions || [],
                isActive: editData.isActive !== false,
                isVerified: editData.isVerified === true,
                phone: editData.phone || '',
                email: editData.email || '',
                dialCode: editData.dialCode || '+1',
                updatedAt: serverTimestamp()
            });
            toast.success('Company updated successfully');
            setEditDialog(false);
            fetchCompany();
        } catch (error) {
            console.error('Error updating company:', error);
            toast.error('Failed to update company');
        }
    };

    const handleToggleBlock = async () => {
        try {
            await updateDoc(doc(db, 'companies', id), {
                isBlocked: !company.isBlocked,
                updatedAt: serverTimestamp()
            });
            toast.success(`Company ${company.isBlocked ? 'unblocked' : 'blocked'} successfully`);
            fetchCompany();
        } catch (error) {
            console.error('Error toggling block:', error);
            toast.error('Failed to update company status');
        }
    };

    const handleDeleteCompany = async () => {
        if (window.confirm('Archive this company and disable all portal access? Historical records will be retained.')) {
            try {
                await updateDoc(doc(db, 'companies', id), {
                    isDeleted: true,
                    archived: true,
                    isActive: false,
                    isBlocked: true,
                    deletedAt: serverTimestamp(),
                    deletedBy: user?.uid || '',
                    updatedAt: serverTimestamp(),
                });
                toast.success('Company archived and access disabled');
                navigate('/companies');
            } catch (error) {
                console.error('Error deleting company:', error);
                toast.error('Failed to delete company');
            }
        }
    };

    const handleToggleVerify = async () => {
        try {
            await updateDoc(doc(db, 'companies', id), {
                isVerified: !company.isVerified,
                verificationDate: !company.isVerified ? serverTimestamp() : null,
                verifiedBy: !company.isVerified ? user?.uid : null,
                updatedAt: serverTimestamp()
            });
            toast.success(`Company ${company.isVerified ? 'unverified' : 'verified'} successfully`);
            fetchCompany();
        } catch (error) {
            console.error('Error toggling verification:', error);
            toast.error('Failed to update verification status');
        }
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setUploadingImage(true);
        try {
            const asset = await uploadAdminFile({ file, scope: 'company', companyId: id, folder: 'company-logos', kind: 'image' });
            const url = asset.url;
            await updateDoc(doc(db, 'companies', id), { logo: url, logoUrl: url, companyLogo: url, companyLogoUrl: url, logoAsset: asset, updatedAt: serverTimestamp() });
            toast.success('Logo uploaded successfully');
            fetchCompany();
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error('Failed to upload logo');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleCoverUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setUploadingCover(true);
        try {
            const asset = await uploadAdminFile({ file, scope: 'company', companyId: id, folder: 'company-covers', kind: 'image' });
            const url = asset.url;
            await updateDoc(doc(db, 'companies', id), { coverImage: url, coverImageUrl: url, companyCover: url, companyCoverUrl: url, coverImageAsset: asset, updatedAt: serverTimestamp() });
            toast.success('Cover image uploaded successfully');
            fetchCompany();
        } catch (error) {
            console.error('Error uploading cover image:', error);
            toast.error('Failed to upload cover image');
        } finally {
            setUploadingCover(false);
        }
    };

    const handleManualSubscriptionSave = async (subscriptionData) => {
        try {
            await updateDoc(doc(db, 'companies', id), {
                subscription: {
                    packageId: subscriptionData.packageId || null,
                    packageName: subscriptionData.packageName || null,
                    price: parseFloat(subscriptionData.amount) || 0,
                    currency: subscriptionData.currency || 'EUR',
                    billingCycle: subscriptionData.billingCycle || 'one-time',
                    status: subscriptionData.status || 'active',
                    assignedAt: serverTimestamp(),
                    assignedBy: user?.uid || '',
                    notes: subscriptionData.notes || '',
                    startDate: subscriptionData.startDate || new Date().toISOString(),
                    validUntil: subscriptionData.billingCycle === 'monthly'
                        ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
                        : subscriptionData.billingCycle === 'annual'
                        ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
                        : null,
                },
                paymentStatus: subscriptionData.status === 'active' ? 'manual' : 'pending',
                paymentAmount: parseFloat(subscriptionData.amount) || 0,
                paymentCurrency: subscriptionData.currency || 'EUR',
                updatedAt: serverTimestamp(),
            });
            toast.success('Subscription assigned successfully');
            setOpenSubscriptionDialog(false);
            fetchCompany();
        } catch (error) {
            console.error('Error assigning subscription:', error);
            toast.error('Failed to assign subscription');
        }
    };

    /* ── render ───────────────────────────────────────────────────────────── */

    const shell = {
        minHeight: '100vh', position: 'relative', fontFamily: fontBody, color: palette.ink,
        background: `linear-gradient(180deg, ${palette.canvas} 0%, ${palette.canvasDeep} 100%)`,
        '&::before': {
            content: '""', position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.4,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27140%27 height=%27140%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.03%27/%3E%3C/svg%3E")',
            animation: 'tfGrain 8s steps(6) infinite',
        },
    };

    if (loading) {
        return (
            <Box sx={shell}>
                <style>{FONT_IMPORT}</style>
                <LiquidBackdrop />
                <Box sx={{ position: 'relative', zIndex: 2 }}></Box>
                <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, py: 10 }}>
                    <GlassPanel accent={palette.rust}>
                        <Box sx={{ display: 'grid', placeItems: 'center', gap: 2, py: 5 }}>
                            <CircularProgress sx={{ color: palette.rust }} />
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.88rem' }}>
                                Loading company details…
                            </Typography>
                        </Box>
                    </GlassPanel>
                </Container>
            </Box>
        );
    }

    if (!company) {
        return (
            <Box sx={shell}>
                <style>{FONT_IMPORT}</style>
                <LiquidBackdrop />
                <Box sx={{ position: 'relative', zIndex: 2 }}></Box>
                <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, py: 10 }}>
                    <GlassPanel accent={palette.danger}>
                        <EmptyState
                            icon={<BusinessIcon />}
                            title="Company not found"
                            text="The company you're looking for doesn't exist or has been removed."
                            action="Back to directory"
                            onAction={() => navigate('/companies')}
                        />
                    </GlassPanel>
                </Container>
            </Box>
        );
    }

    const accent = typeMeta(company.companyType).accent;
    const metrics = company.metrics || {};
    const subscription = company.subscription || null;
    const meetings = companyMeetings || [];
    const payments = paymentProofs || [];
    const team = teamMembers || [];

    const totalMeetings = meetings.length;
    const completedMeetings = meetings.filter(m => m.status === 'completed').length;
    const scheduledMeetings = meetings.filter(m => m.status === 'scheduled' || m.status === 'confirmed').length;
    const avgQuality = meetings.filter(m => m.meetingMetrics?.qualityScore).reduce((acc, m) => acc + (m.meetingMetrics?.qualityScore || 0), 0) /
        (meetings.filter(m => m.meetingMetrics?.qualityScore).length || 1);

    const statCards = [
        {
            label: 'Profile Visits', value: metrics.profileVisits || 0, icon: <TrendingUpIcon  key="config-1082-0-2" />,
            accent: palette.rust, hint: 'Total views',
        },
        {
            label: 'Meetings', value: metrics.meetingsAccepted || 0, icon: <CheckCircleIcon  key="config-1086-1-2" />,
            accent: palette.pine, hint: 'Completed',
        },
        {
            label: 'Engagement', value: metrics.engagementScore || 0, icon: <ShieldIcon  key="config-1090-2-2" />,
            accent: palette.plum, hint: 'Score',
        },
        {
            label: 'Response Rate', value: `${metrics.responseRate || 0}%`, icon: <SpeedIcon  key="config-1094-3-2" />,
            accent: palette.amber, hint: 'Avg response',
        },
        {
            label: 'Team Members', value: team.length, icon: <GroupIcon  key="config-1098-4-2" />,
            accent: palette.teal, hint: 'Active team',
        },
    ];

    return (
        <Box sx={shell}>
            <style>{FONT_IMPORT}</style>
            <LiquidBackdrop />
            <Box sx={{ position: 'relative', zIndex: 2 }}></Box>

            <Container
                maxWidth={false}
                disableGutters
                sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3, lg: 5 }, py: { xs: 3, md: 4 } }}
            >
                {/* ── back + hero ──────────────────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: -14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <IconButton
                            onClick={() => navigate('/companies')}
                            sx={{
                                width: 40, height: 40, borderRadius: '12px',
                                border: `1px solid ${palette.hairline}`,
                                background: rgba('#FFFFFF', 0.6), backdropFilter: 'blur(10px)',
                                transition: 'all .3s ease',
                                '&:hover': { background: rgba('#FFFFFF', 0.9), transform: 'translateX(-4px)' },
                            }}
                        >
                            <ArrowBackIcon sx={{ fontSize: 20, color: palette.inkSoft }} />
                        </IconButton>
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: fontMono, fontSize: '0.64rem', fontWeight: 500,
                                    letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.rust,
                                }}
                            >
                                Company Profile
                            </Typography>
                        </Box>
                    </Box>
                </motion.div>

                {/* ── cover image ──────────────────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box
                        sx={{
                            position: 'relative', width: '100%', height: { xs: 180, md: 260 },
                            borderRadius: '24px', overflow: 'hidden', mb: 3,
                            border: `1px solid ${rgba('#FFFFFF', 0.6)}`,
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.95)}, 0 12px 40px -12px ${rgba(palette.ink, 0.12)}`,
                        }}
                    >
                        {company.coverImage ? (
                            <Box
                                component="img"
                                src={company.coverImage}
                                alt={company.companyName}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: '100%', height: '100%',
                                    background: `linear-gradient(140deg, ${rgba(accent, 0.3)}, ${rgba(accent, 0.12)} 50%, ${rgba(palette.canvasDeep, 0.6)})`,
                                    display: 'grid', placeItems: 'center',
                                }}
                            >
                                <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.4rem', color: rgba('#FFFFFF', 0.6) }}>
                                    {company.companyName}
                                </Typography>
                            </Box>
                        )}
                        <IconButton
                            component="label"
                            sx={{
                                position: 'absolute', bottom: 12, right: 12,
                                bgcolor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(12px)',
                                borderRadius: '12px', color: '#fff', p: '8px 14px',
                                gap: 1, '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                            }}
                        >
                            {uploadingCover ? (
                                <CircularProgress size={18} sx={{ color: '#fff' }} />
                            ) : (
                                <>
                                    <UploadIcon sx={{ fontSize: 16 }} />
                                    <Typography sx={{ fontSize: '0.65rem', fontFamily: fontMono, fontWeight: 600 }}>
                                        Change cover
                                    </Typography>
                                </>
                            )}
                            <input type="file" accept="image/*" hidden onChange={handleCoverUpload} />
                        </IconButton>
                    </Box>
                </motion.div>

                {/* ── company header ──────────────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start', mb: 3.5 }}>
                        <Box sx={{ position: 'relative', flexShrink: 0 }}>
                            <CompanyAvatar company={company} size={96} radius={24} />
                            <IconButton
                                component="label"
                                sx={{
                                    position: 'absolute', bottom: -4, right: -4,
                                    bgcolor: palette.paper, boxShadow: `0 4px 12px ${rgba(palette.ink, 0.12)}`,
                                    width: 32, height: 32, borderRadius: '10px',
                                    '&:hover': { bgcolor: palette.canvas }
                                }}
                            >
                                {uploadingImage ? (
                                    <CircularProgress size={16} sx={{ color: palette.rust }} />
                                ) : (
                                    <UploadIcon sx={{ fontSize: 16, color: palette.inkSoft }} />
                                )}
                                <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                            </IconButton>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 0.75 }}>
                                <Typography
                                    sx={{
                                        fontFamily: fontDisplay, fontWeight: 700,
                                        fontSize: { xs: '1.6rem', md: '2.1rem' },
                                        color: palette.ink, letterSpacing: '-0.03em', lineHeight: 1.1,
                                    }}
                                >
                                    {company.companyName}
                                </Typography>
                                {company.isVerified && (
                                    <VerifiedUserIcon sx={{ fontSize: 22, color: palette.pine }} />
                                )}
                                <LifecyclePill company={company} />
                                <TypePill type={company.companyType} />
                            </Box>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                                {company.headquarters && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <LocationIcon sx={{ fontSize: 15, color: palette.inkLight }} />
                                        <Typography sx={{ color: palette.inkSoft, fontSize: '0.82rem' }}>
                                            {company.headquarters}
                                        </Typography>
                                    </Box>
                                )}
                                {company.website && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <LanguageIcon sx={{ fontSize: 15, color: palette.inkLight }} />
                                        <Typography
                                            component="a"
                                            href={safeExternalUrl(company.website.startsWith('http') ? company.website : `https://${company.website}`, { allowBlob: false }) || undefined}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ color: palette.teal, fontSize: '0.82rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                        >
                                            {company.website.replace(/^https?:\/\//, '')}
                                        </Typography>
                                    </Box>
                                )}
                                {company.email && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <EmailIcon sx={{ fontSize: 15, color: palette.inkLight }} />
                                        <Typography sx={{ color: palette.inkSoft, fontSize: '0.82rem' }}>
                                            {company.email}
                                        </Typography>
                                    </Box>
                                )}
                                {company.phone && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <PhoneIcon sx={{ fontSize: 15, color: palette.inkLight }} />
                                        <Typography sx={{ color: palette.inkSoft, fontSize: '0.82rem' }}>
                                            {company.dialCode || '+1'} {company.phone}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {company.description && (
                                <Typography sx={{ color: palette.inkSoft, fontSize: '0.9rem', lineHeight: 1.6, maxWidth: 680 }}>
                                    {company.description}
                                </Typography>
                            )}

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
                                {(company.regions || []).map((r) => (
                                    <MetaPill key={r} label={r} tone={palette.teal} />
                                ))}
                                {subscription?.packageName && (
                                    <MetaPill
                                        label={`${subscription.packageName} · ${priceLabel(subscription)}`}
                                        tone={palette.plum}
                                        icon={<PackageIcon sx={{ fontSize: 12 }} />}
                                    />
                                )}
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                            <Button
                                onClick={() => setEditDialog(true)}
                                startIcon={<EditIcon />}
                                sx={{
                                    textTransform: 'none', fontWeight: 600, borderRadius: '12px', px: 2,
                                    color: '#fff',
                                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 8px 20px -8px ${rgba(palette.rust, 0.6)}`,
                                    '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                                }}
                            >
                                Edit
                            </Button>
                            <Button
                                onClick={handleToggleVerify}
                                startIcon={company.isVerified ? <BlockIcon /> : <VerifiedUserIcon />}
                                sx={{
                                    textTransform: 'none', fontWeight: 600, borderRadius: '12px', px: 2,
                                    color: '#fff',
                                    background: company.isVerified
                                        ? `linear-gradient(140deg, ${palette.danger}, #b91c1c)`
                                        : `linear-gradient(140deg, ${palette.pine}, ${palette.pineGlow})`,
                                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 8px 20px -8px ${rgba(company.isVerified ? palette.danger : palette.pine, 0.6)}`,
                                }}
                            >
                                {company.isVerified ? 'Unverify' : 'Verify'}
                            </Button>
                            <Tooltip title={company.isBlocked ? 'Unblock' : 'Block'}>
                                <IconButton
                                    onClick={handleToggleBlock}
                                    sx={{
                                        width: 40, height: 40, borderRadius: '12px',
                                        border: `1px solid ${palette.hairline}`,
                                        background: rgba('#FFFFFF', 0.6), backdropFilter: 'blur(10px)',
                                        color: company.isBlocked ? palette.success : palette.danger,
                                        transition: 'all .3s ease',
                                        '&:hover': {
                                            background: rgba('#FFFFFF', 0.9),
                                            borderColor: company.isBlocked ? palette.success : palette.danger,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    {company.isBlocked ? <CheckCircleIcon /> : <BlockIcon />}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton
                                    onClick={handleDeleteCompany}
                                    sx={{
                                        width: 40, height: 40, borderRadius: '12px',
                                        border: `1px solid ${palette.hairline}`,
                                        background: rgba('#FFFFFF', 0.6), backdropFilter: 'blur(10px)',
                                        color: palette.danger,
                                        transition: 'all .3s ease',
                                        '&:hover': {
                                            background: rgba('#FFFFFF', 0.9),
                                            borderColor: palette.danger,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </motion.div>

                {/* ── stats ────────────────────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'grid', gap: 2, mb: 3.5,
                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', xl: 'repeat(5, 1fr)' },
                    }}
                >
                    {statCards.map((s, i) => (
                        <StatCard key={s.label} {...s} delay={i * 0.06} loading={loading} />
                    ))}
                </Box>

                {/* ── tabs ──────────────────────────────────────────────────── */}
                <GlassPanel accent={accent} delay={0.2} padding={{ xs: 1.75, md: 2.5 }}>
                    <Box sx={{ borderBottom: `1px solid ${palette.hairlineSoft}`, px: 1 }}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none', fontWeight: 600, fontSize: '0.78rem',
                                    fontFamily: fontBody, color: palette.inkLight,
                                    minHeight: 44, px: 1.5,
                                    '&.Mui-selected': { color: palette.ink },
                                    '&:hover': { color: accent },
                                },
                                '& .MuiTabs-indicator': { height: 3, borderRadius: 3, backgroundColor: accent },
                            }}
                        >
                            <Tab icon={<BusinessIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Overview" />
                            <Tab icon={<AssessmentIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Services" />
                            <Tab icon={<GroupIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Team" />
                            <Tab icon={<PaymentIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Payments" />
                            <Tab icon={<SubscriptionsIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Subscription" />
                            <Tab icon={<EventNoteIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Meetings" />
                            <Tab icon={<TrendingUpIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Analytics" />
                        </Tabs>
                    </Box>

                    <Box sx={{ px: { xs: 0, sm: 1 } }}>
                        {/* ── OVERVIEW ── */}
                        <TabPanel value={tabValue} index={0}>
                            <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
                                <GlassPanel accent={palette.rust} delay={0} padding={{ xs: 2, md: 2.25 }}>
                                    <PanelHead icon={<BusinessIcon />} title="About" accent={palette.rust} dense />
                                    <Typography sx={{ color: palette.inkSoft, fontSize: '0.86rem', lineHeight: 1.7 }}>
                                        {company.description || 'No description provided.'}
                                    </Typography>
                                    <Box sx={{ mt: 2.5 }}>
                                        <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.inkFaint, mb: 0.75 }}>
                                            Details
                                        </Typography>
                                        <Box sx={{ display: 'grid', gap: 0.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                                            <DetailLine icon={<BusinessIcon />} label="Type" accent={palette.rust} value={typeMeta(company.companyType).label} />
                                            <DetailLine icon={<PeopleIcon />} label="Size" accent={palette.rust} value={company.companySize ? `${company.companySize} employees` : 'Not set'} />
                                            <DetailLine icon={<LocationIcon />} label="HQ" accent={palette.rust} value={company.headquarters || 'Not set'} />
                                            <DetailLine icon={<CalendarIcon />} label="Joined" accent={palette.rust} value={formatDate(company.createdAt)} />
                                            <DetailLine icon={<VerifiedUserIcon />} label="Verified" accent={palette.rust} value={company.isVerified ? formatDate(company.verificationDate) : 'Not verified'} />
                                            <DetailLine icon={<PaymentIcon />} label="Payment" accent={palette.rust} value={<PaymentPill status={company.paymentStatus || 'not_required'} />} />
                                        </Box>
                                    </Box>
                                </GlassPanel>

                                <GlassPanel accent={palette.pine} delay={0.06} padding={{ xs: 2, md: 2.25 }}>
                                    <PanelHead icon={<GroupIcon />} title="Team Summary" accent={palette.pine} dense />
                                    {team.length > 0 ? (
                                        <>
                                            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr 1fr' }}>
                                                <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.pine, 0.06), border: `1px solid ${rgba(palette.pine, 0.12)}` }}>
                                                    <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.4rem', color: palette.pine }}>
                                                        {team.length}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight }}>Total</Typography>
                                                </Box>
                                                <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.rust, 0.06), border: `1px solid ${rgba(palette.rust, 0.12)}` }}>
                                                    <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.4rem', color: palette.rust }}>
                                                        {team.filter(m => m.isPrimaryContact).length}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight }}>Primary</Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ mt: 1.5 }}>
                                                {team.slice(0, 3).map((m, i) => (
                                                    <Box key={m.id || i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: i < 2 ? `1px solid ${palette.hairlineSoft}` : 'none' }}>
                                                        <Avatar sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: palette.teal, fontSize: '0.7rem', fontWeight: 700, color: '#fff' }}>
                                                            {(m.displayName || m.name || 'T').charAt(0).toUpperCase()}
                                                        </Avatar>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: palette.ink }}>
                                                                {m.displayName || m.name || 'Team member'}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '0.65rem', color: palette.inkLight }}>
                                                                {m.role || m.position || 'Employee'}
                                                            </Typography>
                                                        </Box>
                                                        {m.isPrimaryContact && <MetaPill label="Primary" tone={palette.rust} />}
                                                    </Box>
                                                ))}
                                                {team.length > 3 && (
                                                    <Typography sx={{ fontSize: '0.7rem', color: palette.inkLight, mt: 1 }}>
                                                        +{team.length - 3} more
                                                    </Typography>
                                                )}
                                            </Box>
                                        </>
                                    ) : (
                                        <EmptyState icon={<GroupIcon />} title="No team members" text="No employees have been added to this company yet." />
                                    )}
                                </GlassPanel>

                                {subscription && (
                                    <GlassPanel accent={palette.plum} delay={0.12} padding={{ xs: 2, md: 2.25 }} sx={{ gridColumn: { lg: '1 / -1' } }}>
                                        <PanelHead icon={<PackageIcon />} title="Current Subscription" accent={palette.plum} dense />
                                        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}>
                                            <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.plum, 0.06), border: `1px solid ${rgba(palette.plum, 0.12)}` }}>
                                                <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.1rem', color: palette.plum }}>
                                                    {subscription.packageName || 'Unnamed'}
                                                </Typography>
                                                <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight }}>Package</Typography>
                                            </Box>
                                            <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.amber, 0.06), border: `1px solid ${rgba(palette.amber, 0.12)}` }}>
                                                <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.1rem', color: palette.amber }}>
                                                    {priceLabel(subscription)}
                                                </Typography>
                                                <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight }}>Price</Typography>
                                            </Box>
                                            <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.pine, 0.06), border: `1px solid ${rgba(palette.pine, 0.12)}` }}>
                                                <SubscriptionPill status={subscription.status || 'inactive'} />
                                                <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight, mt: 0.5 }}>Status</Typography>
                                            </Box>
                                        </Box>
                                    </GlassPanel>
                                )}
                            </Box>
                        </TabPanel>

                        {/* ── SERVICES ── */}
                        <TabPanel value={tabValue} index={1}>
                            <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
                                <GlassPanel accent={palette.teal} delay={0} padding={{ xs: 2, md: 2.25 }}>
                                    <PanelHead icon={<AssessmentIcon />} title="Service Priorities" accent={palette.teal} dense />
                                    {company.servicePriorities && Object.entries(company.servicePriorities).filter(([_, v]) => v > 0).length > 0 ? (
                                        <Box sx={{ display: 'grid', gap: 1.5 }}>
                                            {Object.entries(company.servicePriorities)
                                                .filter(([_, v]) => v > 0)
                                                .map(([key, value]) => (
                                                    <Box key={key}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: palette.ink }}>
                                                                {key.replace(/_/g, ' ').toUpperCase()}
                                                            </Typography>
                                                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.7rem', fontWeight: 700, color: palette.teal }}>
                                                                {value}%
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ height: 4, borderRadius: 3, bgcolor: palette.hairline, overflow: 'hidden' }}>
                                                            <motion.div
                                                                initial={{ width: 0 }}
                                                                animate={{ width: `${value}%` }}
                                                                transition={{ duration: 0.8, ease: 'easeOut' }}
                                                                style={{ height: '100%', borderRadius: 3, background: `linear-gradient(90deg, ${palette.teal}, ${palette.tealGlow})` }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                ))}
                                        </Box>
                                    ) : (
                                        <Typography sx={{ color: palette.inkLight, fontSize: '0.8rem' }}>No services defined</Typography>
                                    )}
                                </GlassPanel>

                                <GlassPanel accent={palette.amber} delay={0.06} padding={{ xs: 2, md: 2.25 }}>
                                    <PanelHead icon={<LocationIcon />} title="Markets & Regions" accent={palette.amber} dense />
                                    {company.targetMarkets?.length > 0 && (
                                        <>
                                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.inkFaint, mb: 0.75 }}>
                                                Target Markets
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                                                {company.targetMarkets.map((m, i) => (
                                                    <MetaPill key={i} label={m} tone={palette.amber} />
                                                ))}
                                            </Box>
                                        </>
                                    )}
                                    {(company.regions || []).length > 0 && (
                                        <>
                                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.inkFaint, mb: 0.75 }}>
                                                Regions
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                                {(company.regions || []).map((r) => (
                                                    <MetaPill key={r} label={r} tone={palette.teal} />
                                                ))}
                                            </Box>
                                        </>
                                    )}
                                    {!company.targetMarkets?.length && !company.regions?.length && (
                                        <Typography sx={{ color: palette.inkLight, fontSize: '0.8rem' }}>No markets or regions defined</Typography>
                                    )}
                                </GlassPanel>
                            </Box>
                        </TabPanel>

                        {/* ── TEAM ── */}
                        <TabPanel value={tabValue} index={2}>
                            {teamLoading ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <CircularProgress sx={{ color: palette.rust }} />
                                    <Typography sx={{ mt: 2, color: palette.inkLight, fontSize: '0.8rem' }}>Loading team members...</Typography>
                                </Box>
                            ) : team.length > 0 ? (
                                <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                                    {team.map((m, i) => (
                                        <motion.div
                                            key={m.id || i}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: i * 0.04 }}
                                        >
                                            <Paper
                                                sx={{
                                                    p: 2, borderRadius: '16px',
                                                    border: `1px solid ${palette.hairlineSoft}`,
                                                    background: rgba('#FFFFFF', 0.5), backdropFilter: 'blur(8px)',
                                                    transition: 'all .3s ease',
                                                    '&:hover': {
                                                        borderColor: rgba(accent, 0.2),
                                                        boxShadow: `0 8px 24px -12px ${rgba(palette.ink, 0.08)}`,
                                                        transform: 'translateY(-2px)',
                                                    },
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: palette.teal, fontSize: '0.9rem', fontWeight: 700, color: '#fff' }}>
                                                        {(m.displayName || m.name || 'T').charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: palette.ink }}>
                                                                {m.displayName || m.name || 'Team member'}
                                                            </Typography>
                                                            {m.isPrimaryContact && <MetaPill label="Primary" tone={palette.rust} />}
                                                            {m.isAdmin && <MetaPill label="Admin" tone={palette.plum} />}
                                                        </Box>
                                                        <Typography sx={{ fontSize: '0.75rem', color: palette.inkLight }}>
                                                            {m.role || m.position || 'Employee'}
                                                            {m.department && ` · ${m.department}`}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, pt: 1.5, borderTop: `1px solid ${palette.hairlineSoft}` }}>
                                                    {m.email && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <EmailIcon sx={{ fontSize: 13, color: palette.inkLight }} />
                                                            <Typography sx={{ fontSize: '0.7rem', color: palette.inkSoft }}>{m.email}</Typography>
                                                        </Box>
                                                    )}
                                                    {m.phone && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <PhoneIcon sx={{ fontSize: 13, color: palette.inkLight }} />
                                                            <Typography sx={{ fontSize: '0.7rem', color: palette.inkSoft }}>{m.phone}</Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Paper>
                                        </motion.div>
                                    ))}
                                </Box>
                            ) : (
                                <EmptyState icon={<GroupIcon />} title="No team members" text="This company doesn't have any team members yet." />
                            )}
                        </TabPanel>

                        {/* ── PAYMENTS ── */}
                        <TabPanel value={tabValue} index={3}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                                <Button startIcon={<AddIcon />} onClick={() => navigate(`/payments/manual?company=${id}`)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '11px', color: '#fff', background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})` }}>Open payment verification</Button>
                            </Box>
                            {paymentLoading ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <CircularProgress sx={{ color: palette.rust }} />
                                    <Typography sx={{ mt: 2, color: palette.inkLight, fontSize: '0.8rem' }}>Loading payments...</Typography>
                                </Box>
                            ) : payments.length > 0 ? (
                                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                                    {payments.map((p, i) => (
                                        <GlassPanel key={p.id || i} accent={palette.amber} delay={i * 0.04} padding={{ xs: 2, md: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <GlassIcon icon={<PaymentIcon />} color={palette.amber} size={36} />
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: palette.ink }}>
                                                            {p.gateway || 'Payment'}
                                                        </Typography>
                                                        <PaymentPill status={p.status} />
                                                    </Box>
                                                </Box>
                                                <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.2rem', color: palette.amber }}>
                                                    {p.currency || 'EUR'} {p.amount || 0}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'grid', gap: 0.5, gridTemplateColumns: '1fr 1fr' }}>
                                                {p.reference && (
                                                    <>
                                                        <Typography sx={{ fontFamily: fontMono, fontSize: '0.55rem', color: palette.inkFaint }}>Reference</Typography>
                                                        <Typography sx={{ fontFamily: fontMono, fontSize: '0.7rem', color: palette.inkSoft, textAlign: 'right' }}>{p.reference}</Typography>
                                                    </>
                                                )}
                                                {p.paymentDate && (
                                                    <>
                                                        <Typography sx={{ fontFamily: fontMono, fontSize: '0.55rem', color: palette.inkFaint }}>Date</Typography>
                                                        <Typography sx={{ fontSize: '0.7rem', color: palette.inkSoft, textAlign: 'right' }}>{formatDate(p.paymentDate)}</Typography>
                                                    </>
                                                )}
                                            </Box>
                                        </GlassPanel>
                                    ))}
                                </Box>
                            ) : (
                                <EmptyState
                                    icon={<PaymentIcon />}
                                    title="No payments"
                                    text={company.paymentStatus === 'not_required' ? 'Payment is not required for this company.' : 'No payment records found.'}
                                />
                            )}
                        </TabPanel>

                        {/* ── SUBSCRIPTION ── */}
                        <TabPanel value={tabValue} index={4}>
                            <Box sx={{ maxWidth: 560 }}>
                                {subscription ? (
                                    <GlassPanel accent={palette.plum} delay={0} padding={{ xs: 2, md: 2.5 }}>
                                        <PanelHead
                                            icon={<PackageIcon />}
                                            title="Subscription"
                                            accent={palette.plum}
                                            right={
                                                <Button
                                                    size="small"
                                                    onClick={() => setOpenSubscriptionDialog(true)}
                                                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', color: palette.plum, border: `1px solid ${rgba(palette.plum, 0.2)}` }}
                                                >
                                                    Manage
                                                </Button>
                                            }
                                        />
                                        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                                            <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.plum, 0.06), border: `1px solid ${rgba(palette.plum, 0.12)}` }}>
                                                <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.1rem', color: palette.plum }}>
                                                    {subscription.packageName || 'Unnamed'}
                                                </Typography>
                                                <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight }}>Package</Typography>
                                            </Box>
                                            <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.amber, 0.06), border: `1px solid ${rgba(palette.amber, 0.12)}` }}>
                                                <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.1rem', color: palette.amber }}>
                                                    {priceLabel(subscription)}
                                                </Typography>
                                                <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight }}>Price</Typography>
                                            </Box>
                                            <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.pine, 0.06), border: `1px solid ${rgba(palette.pine, 0.12)}` }}>
                                                <SubscriptionPill status={subscription.status || 'inactive'} />
                                                <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight, mt: 0.5 }}>Status</Typography>
                                            </Box>
                                            <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.teal, 0.06), border: `1px solid ${rgba(palette.teal, 0.12)}` }}>
                                                <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1rem', color: palette.teal, textTransform: 'capitalize' }}>
                                                    {subscription.billingCycle || 'One-time'}
                                                </Typography>
                                                <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight }}>Billing</Typography>
                                            </Box>
                                        </Box>
                                        {subscription.validUntil && (
                                            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: '10px', bgcolor: rgba(palette.warning, 0.06), border: `1px solid ${rgba(palette.warning, 0.12)}` }}>
                                                <CalendarIcon sx={{ fontSize: 16, color: palette.warning }} />
                                                <Typography sx={{ fontSize: '0.75rem', color: palette.inkSoft }}>
                                                    Valid until: <strong>{formatDate(subscription.validUntil)}</strong>
                                                </Typography>
                                            </Box>
                                        )}
                                    </GlassPanel>
                                ) : (
                                    <EmptyState
                                        icon={<PackageIcon />}
                                        title="No subscription"
                                        text="This company doesn't have a subscription assigned yet."
                                        action="Assign subscription"
                                        onAction={() => setOpenSubscriptionDialog(true)}
                                    />
                                )}
                            </Box>
                        </TabPanel>

                        {/* ── MEETINGS ── */}
                        <TabPanel value={tabValue} index={5}>
                            {meetingsLoading ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <CircularProgress sx={{ color: palette.rust }} />
                                    <Typography sx={{ mt: 2, color: palette.inkLight, fontSize: '0.8rem' }}>Loading meetings...</Typography>
                                </Box>
                            ) : meetings.length > 0 ? (
                                <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                                    {meetings.map((m, i) => (
                                        <motion.div
                                            key={m.id || i}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: i * 0.04 }}
                                        >
                                            <Paper
                                                sx={{
                                                    p: 2, borderRadius: '16px',
                                                    border: `1px solid ${palette.hairlineSoft}`,
                                                    background: rgba('#FFFFFF', 0.5), backdropFilter: 'blur(8px)',
                                                    transition: 'all .3s ease',
                                                    '&:hover': {
                                                        borderColor: rgba(palette.rust, 0.15),
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: `0 8px 24px -12px ${rgba(palette.ink, 0.06)}`,
                                                    },
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: palette.ink }}>
                                                            {m.title || 'Meeting'}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '0.7rem', color: palette.inkLight, mt: 0.25 }}>
                                                            {m.scheduledAt ? formatDate(m.scheduledAt) : 'Date TBD'}
                                                        </Typography>
                                                        {m.targetCompanyName && (
                                                            <Typography sx={{ fontSize: '0.65rem', color: palette.inkFaint }}>
                                                                with {m.targetCompanyName}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <MetaPill
                                                            label={m.status || 'scheduled'}
                                                            tone={m.status === 'completed' ? palette.success : m.status === 'confirmed' ? palette.teal : m.status === 'cancelled' ? palette.danger : palette.rust}
                                                        />
                                                        {m.meetingMetrics?.qualityScore && (
                                                            <MetaPill label={`⭐ ${m.meetingMetrics.qualityScore}`} tone={palette.amber} />
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Paper>
                                        </motion.div>
                                    ))}
                                </Box>
                            ) : (
                                <EmptyState icon={<EventNoteIcon />} title="No meetings" text="No meetings have been scheduled with this company yet." />
                            )}
                        </TabPanel>

                        {/* ── ANALYTICS ── */}
                        <TabPanel value={tabValue} index={6}>
                            <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                                <GlassPanel accent={palette.rust} delay={0} padding={{ xs: 2, md: 2.25 }}>
                                    <PanelHead icon={<EventNoteIcon />} title="Meeting Stats" accent={palette.rust} dense />
                                    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr 1fr' }}>
                                        <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.rust, 0.06), border: `1px solid ${rgba(palette.rust, 0.12)}` }}>
                                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.4rem', color: palette.rust }}>
                                                {totalMeetings}
                                            </Typography>
                                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight }}>Total</Typography>
                                        </Box>
                                        <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.pine, 0.06), border: `1px solid ${rgba(palette.pine, 0.12)}` }}>
                                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.4rem', color: palette.pine }}>
                                                {completedMeetings}
                                            </Typography>
                                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight }}>Completed</Typography>
                                        </Box>
                                        <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.teal, 0.06), border: `1px solid ${rgba(palette.teal, 0.12)}` }}>
                                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.4rem', color: palette.teal }}>
                                                {scheduledMeetings}
                                            </Typography>
                                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight }}>Scheduled</Typography>
                                        </Box>
                                        <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.amber, 0.06), border: `1px solid ${rgba(palette.amber, 0.12)}` }}>
                                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.4rem', color: palette.amber }}>
                                                {avgQuality > 0 ? Math.round(avgQuality) : '—'}
                                            </Typography>
                                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight }}>Avg Quality</Typography>
                                        </Box>
                                    </Box>
                                </GlassPanel>

                                <GlassPanel accent={palette.pine} delay={0.06} padding={{ xs: 2, md: 2.25 }}>
                                    <PanelHead icon={<GroupIcon />} title="Team Stats" accent={palette.pine} dense />
                                    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr 1fr' }}>
                                        <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.pine, 0.06), border: `1px solid ${rgba(palette.pine, 0.12)}` }}>
                                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.4rem', color: palette.pine }}>
                                                {team.length}
                                            </Typography>
                                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight }}>Total</Typography>
                                        </Box>
                                        <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.rust, 0.06), border: `1px solid ${rgba(palette.rust, 0.12)}` }}>
                                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.4rem', color: palette.rust }}>
                                                {team.filter(m => m.isPrimaryContact).length}
                                            </Typography>
                                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight }}>Primary</Typography>
                                        </Box>
                                        <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.plum, 0.06), border: `1px solid ${rgba(palette.plum, 0.12)}` }}>
                                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.4rem', color: palette.plum }}>
                                                {team.filter(m => m.isAdmin || m.role === 'admin').length}
                                            </Typography>
                                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight }}>Admins</Typography>
                                        </Box>
                                        <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: rgba(palette.teal, 0.06), border: `1px solid ${rgba(palette.teal, 0.12)}` }}>
                                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.4rem', color: palette.teal }}>
                                                {team.filter(m => m.isActive !== false).length}
                                            </Typography>
                                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.58rem', textTransform: 'uppercase', color: palette.inkLight }}>Active</Typography>
                                        </Box>
                                    </Box>
                                </GlassPanel>
                            </Box>
                        </TabPanel>
                    </Box>
                </GlassPanel>
            </Container>

            {/* ── EDIT DIALOG ── */}
            <Dialog
                open={editDialog}
                onClose={() => setEditDialog(false)}
                maxWidth="sm"
                fullWidth
                TransitionComponent={Fade}
                slotProps={{ paper: {
                    sx: {
                        ...liquid(accent, { radius: 28 }),
                        background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 62%, ${rgba(accent, 0.1)})`,
                        maxHeight: '92vh',
                    },
                } }}
            >
                <Box sx={{ p: 2.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${palette.hairlineSoft}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                        <GlassIcon icon={<EditIcon />} color={accent} size={46} />
                        <Box>
                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                                Edit Company
                            </Typography>
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.78rem' }}>
                                Update company profile and settings
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => setEditDialog(false)}
                        sx={{ width: 36, height: 36, borderRadius: '12px', color: palette.inkSoft, bgcolor: rgba(palette.ink, 0.04), '&:hover': { bgcolor: rgba(palette.ink, 0.08) } }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
                <DialogContent sx={{ p: 2.75, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        fullWidth size="small" label="Company Name"
                        value={editData.companyName || ''}
                        onChange={(e) => setEditData({ ...editData, companyName: e.target.value })}
                        sx={glassField(accent)}
                    />
                    <TextField
                        fullWidth size="small" label="Description" multiline rows={3}
                        value={editData.description || ''}
                        onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                        sx={glassField(accent)}
                    />
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                        <FormControl fullWidth size="small" sx={glassField(accent)}>
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={editData.companyType || 'operator'}
                                label="Type"
                                onChange={(e) => setEditData({ ...editData, companyType: e.target.value })}
                            >
                                {COMPANY_TYPES.map((t) => (
                                    <MenuItem key={t.value} value={t.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.accent }} />
                                            {t.label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" sx={glassField(accent)}>
                            <InputLabel>Size</InputLabel>
                            <Select
                                value={editData.companySize || '51-200'}
                                label="Size"
                                onChange={(e) => setEditData({ ...editData, companySize: e.target.value })}
                            >
                                {['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'].map((s) => (
                                    <MenuItem key={s} value={s}>{s} employees</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth size="small" label="Headquarters"
                            value={editData.headquarters || ''}
                            onChange={(e) => setEditData({ ...editData, headquarters: e.target.value })}
                            sx={glassField(accent)}
                        />
                        <TextField
                            fullWidth size="small" label="Website"
                            value={editData.website || ''}
                            onChange={(e) => setEditData({ ...editData, website: e.target.value })}
                            sx={glassField(accent)}
                        />
                        <TextField
                            fullWidth size="small" label="Email"
                            value={editData.email || ''}
                            onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                            sx={glassField(accent)}
                        />
                        <TextField
                            fullWidth size="small" label="Phone"
                            value={editData.phone || ''}
                            onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                            slotProps={{ input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <TextField
                                            value={editData.dialCode || '+1'}
                                            onChange={(e) => setEditData({ ...editData, dialCode: e.target.value })}
                                            size="small"
                                            sx={{ width: 60 }}
                                        />
                                    </InputAdornment>
                                ),
                            } }}
                            sx={glassField(accent)}
                        />
                    </Box>
                    <FormControl fullWidth size="small" sx={glassField(accent)}>
                        <InputLabel>Regions</InputLabel>
                        <Select
                            multiple
                            value={editData.regions || []}
                            label="Regions"
                            onChange={(e) => setEditData({ ...editData, regions: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((r) => <MetaPill key={r} label={r} tone={palette.teal} />)}
                                </Box>
                            )}
                        >
                            {['North America', 'Latin America', 'Europe', 'Asia Pacific', 'Middle East', 'Africa'].map((r) => (
                                <MenuItem key={r} value={r}>{r}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={editData.isActive !== false}
                                    onChange={(e) => setEditData({ ...editData, isActive: e.target.checked })}
                                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: palette.pine }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.pine } }}
                                />
                            }
                            label={<Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: palette.ink }}>Active</Typography>}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={editData.isVerified === true}
                                    onChange={(e) => setEditData({ ...editData, isVerified: e.target.checked })}
                                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: palette.plum }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.plum } }}
                                />
                            }
                            label={<Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: palette.ink }}>Verified</Typography>}
                        />
                    </Box>
                </DialogContent>
                <Box sx={{ px: 2.75, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.25, borderTop: `1px solid ${palette.hairlineSoft}`, background: rgba('#FFFFFF', 0.45) }}>
                    <Button onClick={() => setEditDialog(false)} sx={{ textTransform: 'none', fontWeight: 600, color: palette.inkSoft, borderRadius: '12px' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={handleSaveEdit}
                        startIcon={<SaveIcon />}
                        sx={{
                            textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.75, py: 1, color: '#fff',
                            background: `linear-gradient(140deg, ${accent}, ${rgba(accent, 0.72)})`,
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}, 0 12px 28px -12px ${rgba(accent, 0.95)}`,
                        }}
                    >
                        Save changes
                    </Button>
                </Box>
            </Dialog>

            {/* ── SUBSCRIPTION DIALOG ── */}
            <Dialog
                open={openSubscriptionDialog}
                onClose={() => setOpenSubscriptionDialog(false)}
                maxWidth="sm"
                fullWidth
                TransitionComponent={Fade}
                slotProps={{ paper: {
                    sx: {
                        ...liquid(palette.plum, { radius: 28 }),
                        background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 62%, ${rgba(palette.plum, 0.1)})`,
                        maxHeight: '92vh',
                    },
                } }}
            >
                <Box sx={{ p: 2.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${palette.hairlineSoft}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                        <GlassIcon icon={<SubscriptionsIcon />} color={palette.plum} size={46} />
                        <Box>
                            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                                Assign Subscription
                            </Typography>
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.78rem' }}>
                                For {company.companyName}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => setOpenSubscriptionDialog(false)}
                        sx={{ width: 36, height: 36, borderRadius: '12px', color: palette.inkSoft, bgcolor: rgba(palette.ink, 0.04), '&:hover': { bgcolor: rgba(palette.ink, 0.08) } }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
                <DialogContent sx={{ p: 2.75, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ p: 2, borderRadius: '14px', bgcolor: rgba(palette.info, 0.06), border: `1px solid ${rgba(palette.info, 0.12)}` }}>
                        <Typography sx={{ fontSize: '0.78rem', color: palette.inkSoft }}>
                            Assign a subscription package to this company manually.
                            This will override any existing subscription.
                        </Typography>
                    </Box>

                    <TextField
                        fullWidth size="small" label="Package Name" placeholder="e.g. Premium, Standard"
                        value={editData.packageName || ''}
                        onChange={(e) => setEditData({ ...editData, packageName: e.target.value })}
                        sx={glassField(palette.plum)}
                    />
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                        <TextField
                            fullWidth size="small" label="Amount" type="number" placeholder="0.00"
                            value={editData.amount || ''}
                            onChange={(e) => setEditData({ ...editData, amount: parseFloat(e.target.value) || 0 })}
                            slotProps={{ input: { startAdornment: <InputAdornment position="start">€</InputAdornment> } }}
                            sx={glassField(palette.plum)}
                        />
                        <FormControl fullWidth size="small" sx={glassField(palette.plum)}>
                            <InputLabel>Currency</InputLabel>
                            <Select
                                value={editData.currency || 'EUR'}
                                label="Currency"
                                onChange={(e) => setEditData({ ...editData, currency: e.target.value })}
                            >
                                {['EUR', 'USD', 'GBP', 'INR'].map((c) => (
                                    <MenuItem key={c} value={c}>{c}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" sx={glassField(palette.plum)}>
                            <InputLabel>Billing Cycle</InputLabel>
                            <Select
                                value={editData.billingCycle || 'one-time'}
                                label="Billing Cycle"
                                onChange={(e) => setEditData({ ...editData, billingCycle: e.target.value })}
                            >
                                {['one-time', 'monthly', 'quarterly', 'annual'].map((c) => (
                                    <MenuItem key={c} value={c}>{c}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" sx={glassField(palette.plum)}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={editData.status || 'active'}
                                label="Status"
                                onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                            >
                                {['active', 'pending_payment', 'trial', 'expired', 'cancelled'].map((s) => (
                                    <MenuItem key={s} value={s}>{s}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <TextField
                        fullWidth size="small" label="Notes" multiline rows={2}
                        value={editData.notes || ''}
                        onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                        sx={glassField(palette.plum)}
                    />
                </DialogContent>
                <Box sx={{ px: 2.75, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.25, borderTop: `1px solid ${palette.hairlineSoft}`, background: rgba('#FFFFFF', 0.45) }}>
                    <Button onClick={() => setOpenSubscriptionDialog(false)} sx={{ textTransform: 'none', fontWeight: 600, color: palette.inkSoft, borderRadius: '12px' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={() => {
                            const data = {
                                packageId: null,
                                packageName: editData.packageName,
                                amount: editData.amount || 0,
                                currency: editData.currency || 'EUR',
                                billingCycle: editData.billingCycle || 'one-time',
                                status: editData.status || 'active',
                                notes: editData.notes || '',
                                startDate: new Date().toISOString(),
                            };
                            handleManualSubscriptionSave(data);
                        }}
                        startIcon={<SubscriptionsIcon />}
                        sx={{
                            textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.75, py: 1, color: '#fff',
                            background: `linear-gradient(140deg, ${palette.plum}, ${rgba(palette.plum, 0.72)})`,
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}, 0 12px 28px -12px ${rgba(palette.plum, 0.95)}`,
                        }}
                    >
                        Assign Subscription
                    </Button>
                </Box>
            </Dialog>

            <style>{`
                @keyframes tfPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%      { opacity: .45; transform: scale(.82); }
                }
                @keyframes tfGrain {
                    0%   { transform: translate(0, 0); }
                    25%  { transform: translate(-2%, 1%); }
                    50%  { transform: translate(1%, -2%); }
                    75%  { transform: translate(-1%, -1%); }
                    100% { transform: translate(0, 0); }
                }
                * { scrollbar-width: thin; scrollbar-color: ${rgba(palette.ink, 0.16)} transparent; }
                *::-webkit-scrollbar { width: 6px; height: 6px; }
                *::-webkit-scrollbar-track { background: transparent; }
                *::-webkit-scrollbar-thumb { background: ${rgba(palette.ink, 0.14)}; border-radius: 999px; }
                *::-webkit-scrollbar-thumb:hover { background: ${rgba(palette.ink, 0.26)}; }
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: .001ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: .001ms !important;
                    }
                }
            `}</style>
        </Box>
    );
};

export default CompanyDetails;